import { useState, useEffect, useCallback } from 'react';

interface TextSelection {
  text: string;
  startOffset: number;
  endOffset: number;
  parentElement: HTMLElement | null;
}

interface UseTextSelectionFeedbackOptions {
  // Elemento container para monitorar seleção de texto
  containerRef?: React.RefObject<HTMLElement>;
  
  // Callback quando texto é selecionado
  onTextSelected?: (selection: TextSelection) => void;
  
  // Callback quando seleção é limpa
  onSelectionCleared?: () => void;
  
  // Comprimento mínimo do texto selecionado
  minLength?: number;
  
  // Ativar/desativar
  enabled?: boolean;
}

export function useTextSelectionFeedback({
  containerRef,
  onTextSelected,
  onSelectionCleared,
  minLength = 10,
  enabled = true,
}: UseTextSelectionFeedbackOptions = {}) {
  const [selectedText, setSelectedText] = useState<TextSelection | null>(null);
  const [showFeedbackButton, setShowFeedbackButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });

  const handleSelectionChange = useCallback(() => {
    if (!enabled) return;

    const selection = window.getSelection();
    
    if (!selection || selection.isCollapsed || selection.toString().trim().length < minLength) {
      setSelectedText(null);
      setShowFeedbackButton(false);
      onSelectionCleared?.();
      return;
    }

    const text = selection.toString().trim();
    
    // Verificar se a seleção está dentro do container (se especificado)
    if (containerRef?.current) {
      const range = selection.getRangeAt(0);
      const container = containerRef.current;
      
      if (!container.contains(range.commonAncestorContainer)) {
        setSelectedText(null);
        setShowFeedbackButton(false);
        return;
      }
    }

    // Obter posição para o botão de feedback
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    
    const newSelection: TextSelection = {
      text,
      startOffset: range.startOffset,
      endOffset: range.endOffset,
      parentElement: range.commonAncestorContainer.parentElement,
    };

    setSelectedText(newSelection);
    setButtonPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + window.scrollY + 10,
    });
    setShowFeedbackButton(true);
    onTextSelected?.(newSelection);
  }, [enabled, containerRef, minLength, onTextSelected, onSelectionCleared]);

  // Listener para mudanças de seleção
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [enabled, handleSelectionChange]);

  // Função para limpar seleção
  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelectedText(null);
    setShowFeedbackButton(false);
    onSelectionCleared?.();
  }, [onSelectionCleared]);

  // Obter contexto ao redor do texto selecionado
  const getTextContext = useCallback((maxContextLength: number = 200): string => {
    if (!selectedText?.parentElement) return '';
    
    const fullText = selectedText.parentElement.textContent || '';
    const selectedIndex = fullText.indexOf(selectedText.text);
    
    if (selectedIndex === -1) return fullText.substring(0, maxContextLength);
    
    const contextStart = Math.max(0, selectedIndex - 100);
    const contextEnd = Math.min(fullText.length, selectedIndex + selectedText.text.length + 100);
    
    let context = fullText.substring(contextStart, contextEnd);
    
    if (contextStart > 0) context = '...' + context;
    if (contextEnd < fullText.length) context = context + '...';
    
    return context;
  }, [selectedText]);

  return {
    selectedText,
    showFeedbackButton,
    buttonPosition,
    clearSelection,
    getTextContext,
    isTextSelected: !!selectedText,
  };
}

export type { TextSelection };

